import { useEffect, useState } from 'react';

import { onConsentChange, readConsent, setAnalyticsConsent } from '@/lib/analytics';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import { marketingHref } from '@/lib/marketing';

/**
 * Opt-in prompt for optional identity-linked parent journeys and configured Google Analytics.
 *
 * Rendered only on authenticated parent Portal/setup surfaces, so it can never appear on a kid surface — and
 * nothing is loaded from Google until a parent presses Allow analytics (see analytics.ts).
 * Both answers are sticky: the banner disappears once a choice is stored.
 */
export function PortalAnalyticsConsentBanner({
  showSavedChoice = false,
}: {
  showSavedChoice?: boolean;
}) {
  const { t } = usePortalLocale();
  const [choice, setChoice] = useState(() => readConsent());

  // Keep every mounted instance in sync — and re-render this one — when the
  // choice is made in another tab-local component.
  useEffect(() => onConsentChange(() => setChoice(readConsent())), []);

  if (showSavedChoice ? !choice : !!choice) return null;

  // A compact footer prompt; the full disclosure stays available before either choice.
  return (
    <div
      role="region"
      aria-label={showSavedChoice ? t('Analytics preferences') : t('Analytics choice')}
      className={`${showSavedChoice ? 'mt-4 ' : ''}rounded-2xl border border-hairline bg-surface px-4 py-4 sm:px-6`}
      data-testid={showSavedChoice ? 'portal-analytics-preferences' : 'portal-analytics-consent'}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-4">
        <span className="shrink-0 justify-self-start text-sm font-semibold text-ink-soft">
          {t('Privacy')}
        </span>
        <div className="w-full break-words text-sm leading-relaxed text-ink lg:min-w-0">
          {showSavedChoice && (
            <span role="status" className="mb-2 block font-semibold">
              {choice === 'granted'
                ? t(
                    'Optional analytics: on on this browser. You can change this choice at any time.',
                  )
                : t(
                    'Optional analytics: off on this browser. You can change this choice at any time.',
                  )}
            </span>
          )}
          <p>
            {t(
              'Allow optional analytics to help improve the Parent Portal? This links your browsing and purchases to your parent account. Never children’s activity.',
            )}
          </p>
          <details className="mt-2 text-ink-soft">
            <summary className="w-fit cursor-pointer rounded font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky">
              {t('What we collect')}
            </summary>
            <p className="mt-2">
              {t(
                'Airbotix links parent page categories, course and Demo views, displayed prices, viewport dimensions, scroll positions and fixed visible course sections to your parent account. Its reports combine these with existing registration, login and payment records. If configured, Google Analytics also measures Portal page views, registrations and purchases, including order ID and amount. This optional collection never measures children or sends raw URLs, query strings or child content, form input or screenshots. Viewport observations do not prove reading. Existing first-party app-open and feature-selection counts remain separate.',
              )}{' '}
              <a
                href={marketingHref('/privacy')}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-brand-sky underline"
              >
                {t('Privacy Policy')}
              </a>
            </p>
          </details>
        </div>
        <div
          role="group"
          aria-label={t('Analytics consent choices')}
          className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:justify-self-end lg:shrink-0"
        >
          <button
            type="button"
            aria-pressed={choice === 'declined'}
            onClick={() => setAnalyticsConsent(false)}
            className="min-h-11 min-w-11 w-full shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold text-ink-soft transition-colors hover:bg-brand-sky/10 sm:w-auto"
          >
            {t('Essential only')}
          </button>
          <button
            type="button"
            aria-pressed={choice === 'granted'}
            onClick={() => setAnalyticsConsent(true)}
            className="btn-pill-secondary min-h-11 min-w-11 w-full shrink-0 sm:w-auto"
          >
            {t('Allow analytics')}
          </button>
        </div>
      </div>
    </div>
  );
}
